"use client";

// Renders a rounded-module QR code using qr-code-styling.
// The library is DOM-based, so we mount it via a ref + useEffect after the
// client takes over from SSR.

import QRCodeStyling from "qr-code-styling";
import { useEffect, useRef } from "react";

interface StyledQRProps {
  value: string;
  size?: number;
  color?: string;
  logoUrl?: string;
}

export function StyledQR({
  value,
  size = 208,
  color = "#0040ff",
  logoUrl,
}: StyledQRProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const qrRef = useRef<QRCodeStyling | null>(null);

  useEffect(() => {
    if (!containerRef.current) {
      return;
    }

    if (!qrRef.current) {
      qrRef.current = new QRCodeStyling({
        width: size,
        height: size,
        type: "svg",
        data: value,
        margin: 0,
        qrOptions: { errorCorrectionLevel: "M" },
        dotsOptions: { color, type: "rounded" },
        cornersSquareOptions: { color, type: "extra-rounded" },
        cornersDotOptions: { color, type: "rounded" },
        backgroundOptions: { color: "transparent" },
        ...(logoUrl && {
          image: logoUrl,
          imageOptions: {
            crossOrigin: "anonymous",
            margin: 4,
            hideBackgroundDots: true,
            imageSize: 0.22,
          },
        }),
      });
      containerRef.current.innerHTML = "";
      qrRef.current.append(containerRef.current);
    } else {
      qrRef.current.update({
        data: value,
        width: size,
        height: size,
        dotsOptions: { color, type: "rounded" },
        cornersSquareOptions: { color, type: "extra-rounded" },
        cornersDotOptions: { color, type: "rounded" },
      });
    }
  }, [value, size, color, logoUrl]);

  return <div ref={containerRef} style={{ width: size, height: size }} />;
}
